import { Injectable } from '@angular/core';
import {
  SituViewWebApiClient,
  FileVaultCategory,
  FileVaultSubjectType,
  ListMarketingMaterialsRequest,
  NewUpdateProductRequest,
  UpdateMarketingFileApprovalRequest,
  UpdateMarketingMaterialFiltersRequest,
  MarketingFileReference,
  MarketingRole,
  Product2,
} from 'situview-webapi-client';
import { FileVaultService } from './file-vault.service';

/**
 * Backs the situREACH content manager — the three tiles a prospect sees for a
 * product (Product / Range / Company), reduced to a flat list of content slots
 * that each say what fills them and whether they are live.
 *
 * Scope: every read and write is for one supplier as seen by one partner
 * (builder). Approval is per builder, which is why partnerId is a parameter
 * everywhere rather than page state.
 */

/** Which tile a slot belongs to. */
export type TileLevel = 'product' | 'range' | 'company';

/**
 * How a slot is doing.
 *  - `auto`     the platform fills it; nothing to approve
 *  - `approved` supplier content this partner approved — live now
 *  - `pending`  supplier content waiting on this partner
 *  - `rejected` this partner said no
 *  - `missing`  nothing attached, or no field exists to hold it yet
 */
export type SlotState = 'auto' | 'approved' | 'pending' | 'rejected' | 'missing';

/** The zone of the tile a slot lands in. Drives the schematic callouts. */
export type TileZone =
  | 'header' | 'range' | 'image' | 'supplier'
  | 'sku' | 'name' | 'detail' | 'stats' | 'points' | 'cta';

export interface ContentSlot {
  /** 1-based, stable within a level — the number shown against the schematic. */
  n: number;
  level: TileLevel;
  label: string;
  zone: TileZone;
  state: SlotState;
  /** What is filling the slot today, for display. */
  value: string | null;
  /** Where the value comes from, or why it is empty. */
  note?: string;
  /** Relation id, when the slot is backed by a marketing file. */
  relationId?: string;
  /**
   * True when this partner has its own attachment rather than inheriting the
   * supplier's default — the default row is then not what this partner sees.
   */
  partnerOverride?: boolean;
  /** Set when the slot cannot be filled until a schema change lands. */
  noFieldYet?: boolean;
}

export interface LevelSummary {
  level: TileLevel;
  slots: ContentSlot[];
  filled: number;
  total: number;
  pending: number;
  /**
   * Whether buyers see this tile. The Product tile always shows. Range and
   * Company only go live once every slot is approved (or automatic); any gap
   * keeps the whole tile off the showcase. Same rule as SituReach's
   * isRangeContentReady / isCompanyContentReady.
   */
  live: boolean;
}

@Injectable({ providedIn: 'root' })
export class TileContentService {
  constructor(
    private client: SituViewWebApiClient,
    private fileVault: FileVaultService,
  ) {}

  /**
   * Saves a catalogue field the supplier owns — description, brand, and so on.
   * Takes the whole product because the API replaces it wholesale; callers
   * mutate the copy they already hold and pass it back.
   */
  async saveProduct(product: Product2): Promise<boolean> {
    const request = new NewUpdateProductRequest();
    request.product = product;
    const response = await this.client.newUpdateProduct(request);
    return response?.success === true;
  }

  /**
   * Uploads a file and attaches it to a tile slot in one action.
   *
   * Attaching is a whole-list replace: `updateMarketingMaterialFilters` takes
   * every relation for the subject plus a role map, so the existing
   * attachments have to be sent back alongside the new one or they are
   * dropped. `scopeCompanyId` is the partner for a partner-specific override,
   * or undefined for the supplier's default that every partner inherits.
   */
  async uploadForRole(
    supplierId: string,
    subjectId: string,
    subjectType: FileVaultSubjectType,
    role: MarketingRole,
    file: File,
    existing: MarketingFileReference[],
    scopeCompanyId?: string,
  ): Promise<boolean> {
    // The subject decides which tile picks the file up. SituReach reads the
    // Product tile's hero and document from files on the product, and the
    // showcase deck adds files on the supplier company after them.
    const uploaded = await this.fileVault.uploadBlob(
      supplierId,
      subjectId,
      subjectType,
      FileVaultCategory.MarketingMaterial,
      file,
    );
    const relationId = uploaded?.relationId;
    if (!relationId) {
      return false;
    }

    // This call REPLACES the whole list for the scope being written, so only
    // send back files that already belong to that scope. `existing` comes from
    // a partner-scoped read and therefore mixes the supplier default with that
    // partner's overrides — writing the lot into the default bucket would
    // silently promote one partner's override to every partner.
    const inScope = (f: MarketingFileReference): boolean =>
      (f.filters ?? []).some((flt) => (flt.companyId ?? undefined) === scopeCompanyId);

    const roles: { [id: string]: MarketingRole } = {};
    const relationIds: string[] = [];
    existing.forEach((f) => {
      if (!f.relationId || f.relationId === relationId || !inScope(f)) {
        return;
      }
      relationIds.push(f.relationId);
      roles[f.relationId] = this.roleOf(f, scopeCompanyId ?? '');
    });
    relationIds.push(relationId);
    roles[relationId] = role;

    const request = new UpdateMarketingMaterialFiltersRequest();
    request.companyId = supplierId;
    request.subjectId = subjectId;
    request.filterCompanyId = scopeCompanyId;
    request.fileVaultRelationIds = relationIds;
    request.roles = roles;
    const response = await this.client.updateMarketingMaterialFilters(request);
    return response?.success === true;
  }

  /**
   * Marketing files this supplier holds for a subject. `subjectId` is the
   * product / range / company the files hang off; omit it for everything.
   *
   * `actingCompanyId` is the signed-in user's company, NOT the partner. The API
   * authorises the caller against it, and a supplier user holds no claim on
   * their partner builder, so asking as the partner is refused. It does not
   * narrow the result either way: every builder's approvals come back on each
   * file, and the slot builders pick out the partner's from there.
   *
   * Throws rather than returning an empty list. Uploads rewrite the whole
   * attachment list from this, so a failed read mistaken for "no files" would
   * detach everything already on the subject.
   */
  async listMaterials(
    supplierId: string,
    actingCompanyId: string,
    subjectId?: string,
  ): Promise<MarketingFileReference[]> {
    const response = await this.client.listMarketingMaterials(
      new ListMarketingMaterialsRequest({
        companyId: supplierId,
        requestingCompanyId: actingCompanyId,
        subjectIds: subjectId ? [subjectId] : undefined,
      }),
    );
    if (!response.success) {
      throw new Error(response.errorMessages?.join('; ') || 'Marketing materials could not be loaded.');
    }
    if (!response.marketingFiles) {
      return [];
    }
    // Drop the map/thumbnail sidecars the vault stores alongside real assets.
    return response.marketingFiles.filter(
      (f) => f.filePath !== '/map' && f.filePath !== '/thumbnail',
    );
  }

  /** Records this partner's decision on one attached file. */
  async setApproval(partnerId: string, relationId: string, isApproved: boolean): Promise<boolean> {
    const response = await this.client.createOrUpdateMarketingFileApproval(
      new UpdateMarketingFileApprovalRequest({
        companyId: partnerId,
        fileVaultReferenceCompanySubjectRelationId: relationId,
        isApproved,
      }),
    );
    return response?.success === true;
  }

  /**
   * The Product tile's slots. The first five come from the catalogue and never
   * need approval; the rest are supplier material routed by role, or gaps.
   */
  buildProductSlots(
    product: Product2 | null,
    files: MarketingFileReference[],
    partnerId: string,
    productTypeName: string | null,
  ): ContentSlot[] {
    return [
      {
        n: 1, level: 'product', label: 'Product name', zone: 'name',
        state: product?.name ? 'auto' : 'missing',
        value: product?.name ?? null, note: 'From the catalogue',
      },
      {
        n: 2, level: 'product', label: 'SKU', zone: 'sku',
        state: product?.sku ? 'auto' : 'missing',
        value: product?.sku ?? null,
        note: product?.sku ? 'From the catalogue' : 'No SKU on this product',
      },
      {
        n: 3, level: 'product', label: 'Type', zone: 'detail',
        state: productTypeName ? 'auto' : 'missing',
        value: productTypeName, note: 'From the product type',
      },
      {
        n: 4, level: 'product', label: 'Range', zone: 'range',
        state: product?.brand?.name ? 'auto' : 'missing',
        value: product?.brand?.name ?? null,
        note: product?.brand?.name
          ? 'From the product brand'
          : 'No brand set — the tile renders without a range line',
      },
      {
        n: 5, level: 'product', label: 'Description', zone: 'detail',
        state: product?.description ? 'auto' : 'missing',
        value: product?.description ?? null,
        note: product?.description ? 'From the catalogue' : 'No copy in the catalogue',
      },
      {
        // The auto-generated render: the tile's face when no hero is approved,
        // and the source of the turntable frames and the 3D/AR model.
        n: 6, level: 'product', label: 'Product render', zone: 'image',
        state: product?.previewArtifactId ? 'auto' : 'missing',
        value: product?.previewArtifactId ? 'Generated' : null,
        note: product?.previewArtifactId
          ? 'Auto-generated — used when no hero image is approved'
          : 'Not generated yet — the tile has no image to fall back on',
      },
      this.fileSlot(7, 'product', 'Hero image', 'image', files, partnerId, MarketingRole.Hero,
        'Replaces the auto-generated Situ render as the tile face'),
      this.fileSlot(8, 'product', 'Brochure', 'cta', files, partnerId, MarketingRole.Brochure,
        'Becomes the tile button'),
      this.fileSlot(9, 'product', 'Warranty', 'cta', files, partnerId, MarketingRole.Warranty,
        'Used for the tile button when there is no brochure'),
    ];
  }

  /**
   * The Range tile. Its title comes from the product's brand; the copy and
   * stat cards have nowhere to live yet, so they report as missing with the
   * reason rather than pretending to be editable.
   */
  buildRangeSlots(
    product: Product2 | null,
    files: MarketingFileReference[],
    partnerId: string,
  ): ContentSlot[] {
    // Only files attached to this range. The caller hands over everything the
    // supplier holds, which includes each product's own artwork — without this
    // the Range tile would claim a product's hero as its own.
    const rangeFiles = product?.brandId
      ? files.filter((f) => f.subjectId === product.brandId)
      : [];
    return [
      {
        n: 1, level: 'range', label: 'Range name', zone: 'range',
        state: product?.brand?.name ? 'auto' : 'missing',
        value: product?.brand?.name ?? null, note: 'From the product brand',
      },
      {
        n: 2, level: 'range', label: 'Lead copy', zone: 'detail', state: 'missing', value: null,
        noFieldYet: true, note: 'No field for range copy yet — needs a schema change',
      },
      {
        n: 3, level: 'range', label: 'Stat cards', zone: 'stats', state: 'missing', value: null,
        noFieldYet: true, note: 'No field yet — counts can be derived from the catalogue',
      },
      this.fileSlot(4, 'range', 'Range image', 'image', rangeFiles, partnerId, MarketingRole.Hero,
        'Uploads open once ranges have a settled home (Brand or ProductRanges)'),
      this.fileSlot(5, 'range', 'Range brochure', 'cta', rangeFiles, partnerId, MarketingRole.Brochure,
        'Uploads open once ranges have a settled home (Brand or ProductRanges)'),
    ];
  }

  /** The Company tile — the brand story, as this partner sees it. */
  buildCompanySlots(
    companyName: string | null,
    files: MarketingFileReference[],
    partnerId: string,
    supplierId?: string,
  ): ContentSlot[] {
    // Same rule as the range: only company-level attachments belong here.
    const companyFiles = supplierId
      ? files.filter((f) => f.subjectId === supplierId)
      : [];
    return [
      {
        n: 1, level: 'company', label: 'Company name', zone: 'name',
        state: companyName ? 'auto' : 'missing', value: companyName,
        note: 'From the company record',
      },
      {
        n: 2, level: 'company', label: 'Lead copy', zone: 'detail', state: 'missing', value: null,
        noFieldYet: true, note: 'No field for brand copy yet — needs a schema change',
      },
      {
        n: 3, level: 'company', label: 'Key points', zone: 'points', state: 'missing', value: null,
        noFieldYet: true, note: 'No field yet — three short supplier-authored lines',
      },
      this.fileSlot(4, 'company', 'Brand hero', 'image', companyFiles, partnerId, MarketingRole.Hero,
        'Brand imagery only. Without an approved image the tile shows a plain panel, never the product'),
      this.fileSlot(5, 'company', 'Catalogue', 'cta', companyFiles, partnerId, MarketingRole.Brochure,
        'Becomes the company tile button'),
    ];
  }

  /** Rolls a level's slots up into the counts on the accordion header. */
  summarise(level: TileLevel, slots: ContentSlot[]): LevelSummary {
    return {
      level,
      slots,
      total: slots.length,
      filled: slots.filter((s) => s.state === 'auto' || s.state === 'approved').length,
      pending: slots.filter((s) => s.state === 'pending').length,
      live: level === 'product' || slots.every((s) => s.state === 'auto' || s.state === 'approved'),
    };
  }

  /**
   * The role routing a file to a tile slot. A partner-scoped filter wins over
   * the supplier's default (companyId null); absent role means Gallery, which
   * is the documented fallback for data predating the roles work.
   */
  private roleOf(file: MarketingFileReference, partnerId: string): MarketingRole {
    const filters = file.filters ?? [];
    const chosen = filters.find((f) => f.companyId === partnerId) ?? filters.find((f) => !f.companyId);
    return chosen?.role ?? MarketingRole.Gallery;
  }

  private hasPartnerOverride(file: MarketingFileReference, partnerId: string): boolean {
    return (file.filters ?? []).some((f) => f.companyId === partnerId);
  }

  /** This partner's approval: true / false / undefined (not yet decided). */
  private approvalOf(file: MarketingFileReference, partnerId: string): boolean | undefined {
    return (file.approvals ?? []).find((a) => a.companyId === partnerId)?.isApproved;
  }

  /** A slot fed by whichever attached file carries the given role. */
  private fileSlot(
    n: number,
    level: TileLevel,
    label: string,
    zone: TileZone,
    files: MarketingFileReference[],
    partnerId: string,
    role: MarketingRole,
    note: string,
  ): ContentSlot {
    const match = files.find((f) => this.roleOf(f, partnerId) === role);
    if (!match) {
      return {
        n, level, label, zone, state: 'missing', value: null,
        note: `Nothing attached — ${note.charAt(0).toLowerCase()}${note.slice(1)}`,
      };
    }
    const approved = this.approvalOf(match, partnerId);
    return {
      n, level, label, zone,
      state: approved === true ? 'approved' : approved === false ? 'rejected' : 'pending',
      value: match.fileName ?? null,
      note,
      relationId: match.relationId,
      partnerOverride: this.hasPartnerOverride(match, partnerId),
    };
  }
}
